import {Button, Card, CardBody, CardFooter, CardHeader, Typography} from "@material-tailwind/react";
import React, {useEffect, useState, useCallback, useRef} from "react";
import {submitWorkReport} from "../../../services/report/work";
import {LoadingOverlay} from "../../system/loading_overlay";
import {Toaster} from "../../../helper/Toaster";
import persianNumberFormat from "../../../helper/PersianNumberFormat";
import {WorkReportGrid} from "./grid";
import Dict from "../../../helper/dict";

import SelectInput from "../../system/select";
import {getPersonsNameAndFamily} from "../../../services/profile/profile";
import {getAgentsList} from "../../../services/agent/agent";
import {getFieldBranchesByFieldId} from "../../../services/branch/branch";
import {CardFooterPaginator} from "../../system/cardFooterPaginator";
import FromToDateField from "../../system/fromToDateField";
import {getFields} from "../../../services/field/field";
import {downloadFile} from "../../../config";
import {PaginateContext} from "../../../context/paginate";
import {Modal} from "../../system/modal";
import FromToNumberField from "../../system/fromToNumberField";

export function WorkReportManagement() {

  const [workReport, setWorkReport] = useState([]);
  const [lastDataRefresh, setLastDataRefresh] = useState(null);
  const [gridRowCount, setGridRowCount] = useState(10);
  const [gridCurrentPage, setGridCurrentPage] = useState(1);
  const [gridTotalRowCount, setGridTotalRowCount] = useState(0);
  const [sortBy, setSortBy] = useState("id")
  const [sortType, setSortType] = useState(false);
  const [from, setFrom] = useState((gridCurrentPage - 1) * gridRowCount + 1);
  const [to, setTo] = useState((gridCurrentPage) * gridRowCount);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const debounceTimerRef = useRef(null);

  const [openModal, setOpenModal] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null);

  const [personId, setPersonId] = useState(null);
  const [personName, setPersonName] = useState(null);
  const [personFamily, setPersonFamily] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [fieldId, setFieldId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");
  const [paidAtFrom, setPaidAtFrom] = useState("");
  const [paidAtTo, setPaidAtTo] = useState("");
  const [duration, setDuration] = useState(null);
  const [status, setStatus] = useState(null);
  const [minPrice, setMinPrice] = useState(null);
  const [maxPrice, setMaxPrice] = useState(null);

  useEffect(() => {
    if ((gridCurrentPage) * gridRowCount <= gridTotalRowCount) {
      setTo((gridCurrentPage) * gridRowCount)
    } else {
      setTo(gridTotalRowCount)
    }
    if (gridTotalRowCount === 0) {
      setFrom(0)
    } else {
      setFrom((gridCurrentPage - 1) * gridRowCount + 1)
    }
  }, [gridCurrentPage, gridRowCount, gridTotalRowCount]);

  async function getData(data, paginate) {
    if (loading) return; // Prevent multiple simultaneous requests
    
    try {
      setLoading(true);
      const result = await submitWorkReport(data, paginate);
      if (result.status === 200) {
        const {work_report, total_count} = result.data;
        setWorkReport(work_report || []);
        setGridTotalRowCount(total_count);
      } else {
        Toaster.failure(null, <Typography variant="small">
          {result.data?.message || 'خطا در دریافت گزارش'}
        </Typography>);
      }
    } catch (err) {
      Toaster.failure(null, <Typography variant="small">
        {err.message || 'خطا در دریافت گزارش'}
      </Typography>);
      console.error('Work report fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  const filter = () => {
    const data = {}
    if (personId) {
      data.person_id = personId;
    } else if (personName || personFamily) {
      // If individual name/family filters are used, we'll need to handle this differently
      // For now, prioritize the main personId if set
    }

    if (agentId) {
      data.agent_id = agentId;
    }

    if (fieldId) {
      data.field_id = fieldId;
    }

    if (branchId) {
      data.branch_id = branchId;
    }

    if (createdAtFrom) {
      data.created_at_from = createdAtFrom;
    }

    if (createdAtTo) {
      data.created_at_to = createdAtTo;
    }

    if (paidAtFrom) {
      data.paid_at_from = paidAtFrom;
    }

    if (paidAtTo) {
      data.paid_at_to = paidAtTo;
    }

    if (status) {
      data.status = status;
    }

    if (minPrice) {
      data.min_price = parseInt(minPrice);
    }
    if (maxPrice) {
      data.max_price = parseInt(maxPrice);
    }

    return data
  };

  const handleRowClick = (work) => {
    setSelectedWork(work);
    setOpenModal(true);
  };

  const debouncedGetData = useCallback((data, paginate) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      getData(data, paginate);
    }, 500); // 500ms debounce
  }, []);

  useEffect(() => {
    const data = filter()
    const paginate = new URLSearchParams({
      "sort_by": sortBy,
      "sort_type": sortType === false ? "DESC" : "ASC",
      "limit": parseInt(gridRowCount),
      "offset": (parseInt(gridCurrentPage) - 1) * parseInt(gridRowCount),
    }).toString()

    // For pagination and sorting, call immediately
    if (gridCurrentPage !== 1 || sortBy !== 'id' || sortType !== false) {
      getData(data, paginate);
    } else {
      // For filters, use debouncing
      debouncedGetData(data, paginate);
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [personId, agentId, fieldId, branchId, createdAtFrom, createdAtTo, paidAtFrom, paidAtTo, status, minPrice, maxPrice, gridCurrentPage, gridRowCount, sortBy, sortType, debouncedGetData]);

  return (
    <PaginateContext.Provider value={{
      setSortBy: setSortBy,
      sortType: sortType,
      setSortType: setSortType,
      active: gridCurrentPage,
      setActive: setGridCurrentPage,
      totalRows: gridTotalRowCount,
      rowsCount: gridRowCount,
      setRowsCount: setGridRowCount,
      from: from,
      to: to,
    }}>
      <div className="w-full">
        <Card className="pt-8 w-full">
          <CardHeader floated={true} shadow={false} className="rounded-none w-full">
            <div className="mb-8 flex items-center justify-between gap-8">
              <Typography variant="h5" color="blue-gray">سرویس‌ها</Typography>
            </div>

          </CardHeader>
          <CardBody className="w-full">

            <div className="grid grid-cols-1 gap-4 mb-8 pb-4 border-b-2 md:grid-cols-3 xxl:grid-cols-3">

              <div className="flex flex-col">
                <SelectInput
                  label={"کاربر"}
                  defaultValue={personId}
                  refreshSignal={personId}
                  autoLoadService={getPersonsNameAndFamily}
                  autoLoadRender={({name, family}) => {
                    return name + " " + family
                  }}
                  onChange={setPersonId}
                />
              </div>
              <div className="flex flex-col">
                <SelectInput
                  label={"مباشر"}
                  defaultValue={agentId}
                  refreshSignal={agentId}
                  // multiselect={false}
                  autoLoadService={getAgentsList}
                  autoLoadRender={({name, family}) => {
                    return name + " " + family
                  }}
                  onChange={setAgentId}
                />
              </div>

              <div className="flex flex-col">
                <SelectInput
                  label={"رشته"}
                  defaultValue={fieldId}
                  autoLoadService={getFields}
                  onChange={setFieldId}
                />
              </div>

              <div className="flex flex-col">
                <SelectInput
                  label={"گرایش"}
                  defaultValue={branchId}
                  refreshSignal={fieldId}
                  autoLoadService={getFieldBranchesByFieldId}
                  autoLoadServiceData={fieldId}
                  autoLoadRender={({title}) => {
                    return title
                  }}
                  onChange={setBranchId}
                />
              </div>

            </div>

            <div className="flex flex-col md:flex-row">

              <div className="flex flex-row w-1/2 pb-4 mb-4 md:flex-col">
                <FromToDateField
                  className="w-full"
                  fromDate={createdAtFrom}
                  setFromDate={setCreatedAtFrom}
                  toDate={createdAtTo}
                  setToDate={setCreatedAtTo}
                  label="ایجاد"
                />
              </div>

              <div className="flex flex-col w-1/2 mb-4 md:flex-col">
                <FromToDateField
                  className="w-full"
                  fromDate={paidAtFrom}
                  setFromDate={setPaidAtFrom}
                  toDate={paidAtTo}
                  setToDate={setPaidAtTo}
                  label="پرداخت"
                />
              </div>

            </div>

            <div className="flex flex-row justify-between">
              <Button variant="gradient" color="gray" onClick={() => {
                setPersonId(null)
                setAgentId(null)
                setFieldId(null)
                setBranchId(null)
                setCreatedAtFrom(null)
                setCreatedAtTo(null)
                setPaidAtFrom(null)
                setPaidAtTo(null)
                setDuration(null);
                setStatus(null);
                setMinPrice(null);
                setMaxPrice(null);
              }} className="ml-1" disabled={loading}>
                <span>حذف فیلتر</span>
              </Button>

              {workReport.length > 0 &&
                <Button size="sm" variant="outlined" onClick={async () => {
                  try {
                    setIsExporting(true);
                    const data = filter();
                    downloadFile("work_report_excel", data, "works")
                  } catch (err) {
                    Toaster.failure(null, <Typography variant="small">
                      خطا در خروجی گرفتن
                    </Typography>);
                    console.error('Error exporting:', err);
                  } finally {
                    setIsExporting(false);
                  }
                }} disabled={isExporting}>
                  {isExporting ? 'در حال خروجی...' : 'خروجی اکسل'}
                </Button>
              }
            </div>


            {loading && <LoadingOverlay />}

            <div className="w-full overflow-x-scroll relative">
              <WorkReportGrid
                workReport={workReport}
                onRowClick={handleRowClick}
              />
            </div>
          </CardBody>

          <CardFooterPaginator/>
        </Card>
      </div>

      {/* Work Details Modal */}
      <Modal 
        open={openModal} 
        setOpen={setOpenModal}
        children={{
          header: selectedWork ? `جزئیات سرویس - ${selectedWork.person_name}` : "جزئیات سرویس",
          body: selectedWork && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">کاربر</p>
                  <p className="font-semibold">{selectedWork.person_name}</p>
                  <p className="text-sm">{persianNumberFormat(selectedWork.person_phone)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">مباشر</p>
                  <p className="font-semibold">{selectedWork.agent_name}</p>
                  <p className="text-sm">{persianNumberFormat(selectedWork.agent_phone)}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">نوع سرویس</p>
                    <p className="font-semibold">{Dict(selectedWork.type)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">وضعیت</p>
                    <p className="font-semibold">
                      {selectedWork.status === 'new' ? "جدید" :
                        selectedWork.status === 'waiting' ? 'در انتظار پرداخت' :
                          selectedWork.status === 'payment' ? 'درحال انجام' :
                            selectedWork.status === 'finish' ? 'اتمام خدمت' :
                              selectedWork.status === 'enable' ? 'فعال' :
                                selectedWork.status === 'disable' ? 'غیر فعال' :
                                  selectedWork.status}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">رشته</p>
                    <p className="font-semibold">{selectedWork.field_title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">گرایش</p>
                    <p className="font-semibold">{selectedWork.branch_title}</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">مبلغ</p>
                    <p className="font-semibold text-lg text-green-600">
                      {persianNumberFormat(selectedWork.price, true)} تومان
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">تاریخ ایجاد</p>
                    {selectedWork.created_at?.length > 0 && (
                      <>
                        <p className="font-semibold">{persianNumberFormat(selectedWork.created_at.split(" ")[0])}</p>
                        <p className="text-sm">{persianNumberFormat(selectedWork.created_at.split(" ")[1])}</p>
                      </>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">تاریخ پرداخت</p>
                    {selectedWork.paid_at?.length > 0 ? (
                      <>
                        <p className="font-semibold">{persianNumberFormat(selectedWork.paid_at.split(" ")[0])}</p>
                        <p className="text-sm">{persianNumberFormat(selectedWork.paid_at.split(" ")[1])}</p>
                      </>
                    ) : (
                      <p className="text-gray-500">پرداخت نشده</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }}
      />
    </PaginateContext.Provider>
  )
    ;
}